import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { getServerErrors } from '../../../../common/helpers';
import UI from '../../../../common/UI';
import useEditEventMutation from './useEditEventMutation';
import useIntegerInput from '../../../../common/useIntegerInput';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

const editEventRestrictionsSchema = yup.object();

export interface EditEventRestrictionsFormProps {
  event: Event;
}

const EditEventRestrictionsForm = ({ event }: EditEventRestrictionsFormProps) => {
  const { t } = useTranslation();
  const integerInput = useIntegerInput();

  const getValues = (event: Event) => ({
    show_ticket_availabilities: event.show_ticket_availabilities,
    show_ticket_availabilities_under: event.show_ticket_availabilities_under,
    age_threshold_date: event.age_threshold_date,
    max_team_size: event.max_team_size,
    private_teams: event.private_teams,
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      restrictions: form.values,
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventRestrictionsSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'restrictions'),
  });

  return (
    <UI.Form onSubmit={() => editEvent()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('ticket_availability')}</UI.Legend>
          <UI.InputDescription>
            {t('event_ticket_availability_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup>
          <UI.FormGrid>
            <UI.Checkbox
              {...form.checkbox('show_ticket_availabilities')}
              sc={{ box: true }}
              id="ShowTicketAvailabilities"
            >
              <UI.Strong sc={{ block: true }}>
                {t('enable_ticket_availabilities')}
              </UI.Strong>
              {t('enable_ticket_availabilities_description')}
            </UI.Checkbox>
          </UI.FormGrid>
        </UI.InputGroup>

        {form.values.show_ticket_availabilities && (
          <UI.InputGroup
            sc={{
              valid: !errors.show_ticket_availabilities_under,
              touched: form.touched.show_ticket_availabilities_under,
            }}
          >
            <UI.InputLabel htmlFor="ShowTicketAvailabilitiesUnder">
              {t('show_ticket_availabilities_under')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('show_ticket_availabilities_under_description')}
            </UI.InputDescription>
            <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
              <UI.DebouncedInput
                {...form.text('show_ticket_availabilities_under', {
                  inputHandler: integerInput(),
                })}
                maxLength={10}
                format={integerInput().format}
                inputMode="numeric"
                match={/[\d]/g}
                placeholder={t('always_show')}
                id="ShowTicketAvailabilitiesUnder"
              />
            </UI.FormGrid>
          </UI.InputGroup>
        )}

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('age_restrictions')}</UI.Legend>
          <UI.InputDescription>
            {t('event_age_restrictions_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
          <UI.InputGroup>
            <UI.InputLabel htmlFor="AgeThresholdDate_Day">
              {t('age_threshold_date')}
            </UI.InputLabel>
            <UI.DateInput
              value={form.values.age_threshold_date}
              onChange={(value) => form.set('age_threshold_date', value)}
              id="AgeThresholdDate"
              hideYear
            />
            <UI.ErrorMessages
              attribute={t('age_threshold_date')}
              errors={errors.age_threshold_date}
            />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('teams')}</UI.Legend>
          <UI.InputDescription>
            {t('edit_event_teams_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.InputGroup>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            <UI.Radio
              {...form.radio('private_teams', false)}
              sc={{ box: true }}
            >
              <UI.Strong sc={{ block: true }}>
                {t('disable_private_teams')}
              </UI.Strong>
              {t('disable_private_teams_description')}
            </UI.Radio>
            <UI.Radio
              {...form.radio('private_teams', true)}
              sc={{ box: true }}
            >
              <UI.Strong sc={{ block: true }}>
                {t('enable_private_teams')}
              </UI.Strong>
              {t('enable_private_teams_description')}
            </UI.Radio>
          </UI.FormGrid>
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.InputLabel htmlFor="MaxTeamSize">{t('max_team_size')}</UI.InputLabel>
          <UI.InputDescription>
            {t('edit_team_sizes_description')}
          </UI.InputDescription>
          <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
            <UI.Div>
              <UI.DebouncedInput
                {...form.text('max_team_size', { inputHandler: integerInput() })}
                format={integerInput().format}
                inputMode="numeric"
                match={/[\d]/g}
                id="MaxTeamSize"
              />
            </UI.Div>
          </UI.FormGrid>
        </UI.InputGroup>
        <UI.ErrorMessages
          attribute={t('max_team_size')}
          errors={errors.max_team_size}
        />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventRestrictionsForm;
